import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { scrollTop } from '@share/utils';
import { Routes } from '@share/constants';
import { CloseSvg } from '@share/assets';

import './style.scss';

interface RouteParams {
  accountUsername: string;
}

const CampingsWelcomePage: React.FC = () => {
  const [isAnnouncementClosed, setIsAnnouncementClosed] = useState(false);

  useEffect(() => {
    scrollTop();
  }, []);
  const history = useHistory();
  const params = useParams<RouteParams>();
  const { accountUsername } = params;

  const navigateToCampings = () => {
    history.push(`/${accountUsername}${Routes.Campings}`);
  };

  const navigateToFAQs = () => {
    history.push(`/${accountUsername}${Routes.CampingsFAQs}`);
  };

  const navigateToBookingHowTo = () => {
    history.push(`/${accountUsername}${Routes.CampingsBookingHowTo}`);
  };

  // Icon Components
  const CalendarIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );

  const CheckCircleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  );

  const ShieldIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );

  const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );

  return (
    <div className="campings-welcome-page">
      <div className="campings-welcome">
        {/* Hero Section */}
        <section className="campings-welcome__hero-section">
          <div className="container">
            <div className="campings-welcome__hero-content">
              <div className="campings-welcome__badge">
                Big News
              </div>
              <h1 className="campings-welcome__hero-title">
                Welcome to AOR/UCN
              </h1>
              <p className="campings-welcome__hero-subtitle">
                Thanks for being a member. We're excited to share what's new and what's next.
              </p>
            </div>
          </div>
        </section>

        {/* Acquisition Announcement */}
        {!isAnnouncementClosed && (
          <section className="campings-welcome__section">
            <div className="container">
              <div className="campings-welcome__announcement-card">
                <button
                  className="campings-welcome__announcement-close"
                  onClick={() => setIsAnnouncementClosed(true)}
                  aria-label="Close announcement"
                >
                  <CloseSvg />
                </button>
                <h2 className="campings-welcome__section-title">AOR/UCN Acquired by RSI Vacations</h2>
                <p className="campings-welcome__section-text">
                  Our shared mission is simple: make AOR one of the premier leaders in the campground space— and then go
                  further. With the backing of RSI's programs and a large, active member base, we're investing in better
                  technology, broader campground partnerships, and more valuable member benefits.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* What's Changing Section */}
        <section className="campings-welcome__section">
          <div className="container">
            <h2 className="campings-welcome__section-title campings-welcome__section-title--center">
              What's Changing (for the better)
            </h2>
            <div className="campings-welcome__features-grid">
              <div className="campings-welcome__feature-card">
                <div className="campings-welcome__feature-icon">
                  <CalendarIcon />
                </div>
                <h3 className="campings-welcome__feature-title">Streamlined Online Booking</h3>
                <p className="campings-welcome__feature-text">
                  From "phone tag" to streamlined online. Log in, search, and book directly with campgrounds using
                  members‑only discount codes. Faster, clearer, 24/7.
                </p>
              </div>

              <div className="campings-welcome__feature-card">
                <div className="campings-welcome__feature-icon">
                  <CheckCircleIcon />
                </div>
                <h3 className="campings-welcome__feature-title">Fresh Deals Added Often</h3>
                <p className="campings-welcome__feature-text">
                  We're onboarding new affiliates daily, weekly, and monthly—so check back frequently for new
                  destinations, seasonal specials, and off‑peak value plays.
                </p>
              </div>

              <div className="campings-welcome__feature-card">
                <div className="campings-welcome__feature-icon">
                  <ShieldIcon />
                </div>
                <h3 className="campings-welcome__feature-title">Clear Rules, Fewer Surprises</h3>
                <p className="campings-welcome__feature-text">
                  Each campground card shows deal details, blackout dates, site types, contact info, and a map—everything
                  you need in one place.
                </p>
              </div>
            </div>
            <div className="campings-welcome__cta-center">
              <button className="campings-welcome__button campings-welcome__button--primary campings-welcome__button--prominent" onClick={navigateToCampings}>
                Browse Campgrounds
              </button>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="campings-welcome__section campings-welcome__section--muted">
          <div className="container">
            <h2 className="campings-welcome__section-title campings-welcome__section-title--center">
              How It Works
            </h2>
            <div className="campings-welcome__how-it-works-card">
              <div className="campings-welcome__step">
                <div className="campings-welcome__step-number">1</div>
                <p className="campings-welcome__step-text">Log in to view members‑only content.</p>
              </div>
              <div className="campings-welcome__step">
                <div className="campings-welcome__step-number">2</div>
                <p className="campings-welcome__step-text">Pick your state/region and open a campground card.</p>
              </div>
              <div className="campings-welcome__step">
                <div className="campings-welcome__step-number">3</div>
                <p className="campings-welcome__step-text">
                  Review the deal and promo code, then click through to the campground website to book (or call if
                  online booking isn't available).
                </p>
              </div>
              <div className="campings-welcome__step">
                <div className="campings-welcome__step-number">4</div>
                <p className="campings-welcome__step-text">Apply your code at checkout to receive your member benefit.</p>
              </div>
              <div className="campings-welcome__pro-tip">
                <p className="campings-welcome__pro-tip-text">
                  <strong>Pro tip:</strong> Codes may rotate periodically to protect partner
                  rate integrity. Always pull the latest code from your dashboard before booking.
                </p>
              </div>
            </div>
            <div className="campings-welcome__cta-center">
              <button className="campings-welcome__button campings-welcome__button--primary campings-welcome__button--prominent" onClick={navigateToBookingHowTo}>
                View Detailed Booking Guide
                <ArrowRightIcon />
              </button>
            </div>
          </div>
        </section>

        {/* Membership Tiers */}
        <section className="campings-welcome__section">
          <div className="container">
            <h2 className="campings-welcome__section-title campings-welcome__section-title--center">
              Gold vs. Platinum Memberships
            </h2>
            <div className="campings-welcome__membership-grid">
              <div className="campings-welcome__membership-card campings-welcome__membership-card--gold">
                <div className="campings-welcome__membership-badge campings-welcome__membership-badge--gold">
                  Gold
                </div>
                <h3 className="campings-welcome__membership-title">Camping Platform Access</h3>
                <p className="campings-welcome__membership-text">
                  Access to the camping platform: members‑only campground deals and promo codes.
                </p>
              </div>

              <div className="campings-welcome__membership-card campings-welcome__membership-card--platinum">
                <div className="campings-welcome__membership-popular">POPULAR</div>
                <div className="campings-welcome__membership-badge campings-welcome__membership-badge--platinum">
                  Platinum
                </div>
                <h3 className="campings-welcome__membership-title">Everything + More</h3>
                <p className="campings-welcome__membership-text">
                  Everything in Gold plus the broader RSI Vacations travel suite: hotels, condos, cruises, curated
                  getaways, and concierge support.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* RSI Vacations Section */}
        <section className="campings-welcome__section campings-welcome__section--primary-light">
          <div className="container">
            <div className="campings-welcome__rsi-content">
              <h2 className="campings-welcome__section-title campings-welcome__section-title--center">
                Powered by RSI Vacations
              </h2>
              <p className="campings-welcome__rsi-text">
                The addition of RSI Vacations means more ways to travel. If you'd like to expand your benefits beyond
                camping, Platinum unlocks a diverse mix of travel savings and personalized planning—perfect for the trips
                before, after, or between your campground adventures.
              </p>
            </div>
          </div>
        </section>

        {/* Our Promise */}
        <section className="campings-welcome__section">
          <div className="container">
            <div className="campings-welcome__promise-content">
              <h2 className="campings-welcome__section-title campings-welcome__section-title--center">
                Our Promise
              </h2>
              <p className="campings-welcome__promise-text">
                We're committed to being more agile, more transparent, and more helpful—from faster updates and cleaner
                search to stronger partnerships that keep value flowing to you. The future of camping at AOR/UCN is bright,
                and we're thrilled to have you with us.
              </p>
              <p className="campings-welcome__promise-text">
                If you ever need help with site access, code visibility, or listing details, contact Member Support from
                your dashboard. For reservations, billing, or campground policies, please reach out to the campgrounds
                directly.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="campings-welcome__section">
          <div className="container">
            <div className="campings-welcome__cta-card">
              <h2 className="campings-welcome__cta-title">Welcome back to AOR/UCN</h2>
              <p className="campings-welcome__cta-subtitle">Bigger, better, and built for you.</p>
              <div className="campings-welcome__cta-buttons">
                <button className="campings-welcome__button campings-welcome__button--secondary" onClick={navigateToCampings}>
                  Browse Campgrounds
                </button>
                <button className="campings-welcome__button campings-welcome__button--outline" onClick={navigateToFAQs}>
                  Read FAQs
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export { CampingsWelcomePage };
